import { useEffect, useRef, useState, type ReactNode } from 'react'

import {
  fetchCheckoutState,
  fetchQuote,
  openCheckout,
  type CheckoutBody,
  type TransportOptions,
  type TransportQuote,
  type TripChoice,
} from '../../api/transport'
import { Button } from '../../components/Button'
import { PageBand, PageShell } from '../../components/PageShell'
import { ApiError } from '../../lib/api'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { formatPesewas } from '../../lib/money'
import {
  EMPTY_BOOKING,
  useBooking,
  type Booking,
  type Details,
  type CarriedTrip,
  type Trip,
} from './booking'
import { carriedFromBooking } from './carry'
import { ConfirmationScreen } from './ConfirmationScreen'
import { Desk } from './Desk'
import { useFocusOnMount } from './focus'
import { ProgressBar } from './ProgressBar'
import { MOBILITY, SERVICE_NAME } from './services'
import { TransportHeader } from './TransportHeader'
import { dayLabel, dayOf, timeOf } from './tripTimes'

/**
 * Screen 5, review, fare and pay (M39-T13, spec §C.3.5, §D.3). The whole
 * trip, the server's fare (never a sum the page made), the policy, the
 * non-emergency line, the terms tick, and one "Pay GHS … & confirm" button
 * (M39-Q2). Pay opens a checkout (M39-T04b); its state is polled (T05).
 *
 * No answer here ever says "pending" or "provisional" (Decision 1): a
 * checkout is not a booking until payment confirms it. Until M41 the payment
 * is not connected, and Pay says so: nothing is charged.
 */

/** How often the checkout's state is asked while the payer approves. */
export const POLL_MS = 3000

/** After the hold's end by this device's clock, how long a broken
 * connection is still retried. The server's own answer is what ends a
 * waiting checkout: a phone clock running fast must not. */
export const GRACE_MS = 120_000

export type ScreenName = 'service' | 'trip' | 'details'

type Keys = { idempotency: string; manage: string }

/** New keys: made when the screen opens, and again only for a new checkout
 * (after a closed or not-completed one). A retry of the same checkout keeps
 * them, so a second tap or a slow network can never open two. */
const newKeys = (): Keys => ({
  idempotency: crypto.randomUUID(),
  manage: `${crypto.randomUUID()}${crypto.randomUUID()}`.replaceAll('-', ''),
})

type Phase =
  | { kind: 'review' }
  | { kind: 'sending' }
  | {
      kind: 'waiting'
      reference: string
      expiresAt: number
      /** The trip, for Screen 6's "Book the same trip again" (M40-T11). */
      carried: CarriedTrip | null
    }
  | { kind: 'not_completed' }
  | { kind: 'confirmed'; reference: string; carried: CarriedTrip | null }
  | {
      kind: 'refused'
      code: string
      message: string
      /** Retry with the same keys (the same checkout), or new ones. */
      retry: 'same' | 'new' | null
    }

type QuoteState =
  | { kind: 'loading' }
  | { kind: 'ready'; quote: TransportQuote }
  | { kind: 'failed' }

const legend = 'font-mono text-[10px] uppercase tracking-wide text-ink-3'
const linkButton =
  'text-[12.5px] font-medium text-accent underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

/** The trip as the quote and the checkout take it. */
function tripChoice(booking: Booking): TripChoice | null {
  const { service, trip, details } = booking
  if (!service || !trip) return null
  return {
    service,
    pickup_area_id: trip.pickupAreaId,
    hospital_id: trip.hospitalId,
    ...(trip.hireLength
      ? { hire_length: trip.hireLength }
      : { journey: trip.journey ?? 'one_way' }),
    wheelchair_needed: details?.wheelchairNeeded ?? false,
    companion: details?.companion ?? 'none',
  }
}

function checkoutBody(
  gatePassedAt: string,
  booking: Booking,
  choice: TripChoice,
  trip: Trip,
  details: Details,
  manageKey: string,
): CheckoutBody {
  return {
    trip: choice,
    mobility: booking.mobility ?? 'walks_unaided',
    uses_oxygen: booking.usesOxygen === true,
    gate_passed_at: gatePassedAt,
    booker_first_name: details.bookerFirstName,
    booker_last_name: details.bookerLastName,
    booker_phone: details.bookerPhone,
    passenger_is_booker: details.passengerIsBooker,
    passenger_name: details.passengerName,
    passenger_age: details.passengerAge,
    passenger_phone: details.passengerPhone,
    own_equipment: [
      ...(details.ownWheelchair ? (['own_wheelchair'] as const) : []),
      ...(details.ownOxygen ? (['own_oxygen'] as const) : []),
    ],
    driver_notes: details.driverNotes,
    consent_given: details.consentGiven,
    pickup_address: trip.pickupAddress,
    pickup_landmark: trip.pickupLandmark,
    pickup_lat: trip.pickupLat,
    pickup_lng: trip.pickupLng,
    appointment_at: trip.appointmentAt,
    pickup_at: trip.pickupAt,
    return_mode: trip.returnMode,
    return_pickup_at: trip.returnPickupAt,
    hire_stops: trip.hireStops,
    terms_accepted: true,
    manage_key: manageKey,
  }
}

export function ReviewScreen({
  options,
  onBack,
  onChange,
  onStop,
  onBookAgain,
  goTo = (url) => window.location.assign(url),
}: {
  options: TransportOptions
  onBack: () => void
  /** "Change" on a section: back to its screen, answers kept. */
  onChange: (screen: ScreenName) => void
  /** 112 from this screen ends the booking (not counted: only the gate is). */
  onStop: () => void
  /** Screen 6's "Book the same trip again" (M40-T11). */
  onBookAgain?: (carried: CarriedTrip) => void
  /** Sends the payer to the provider's page (a test passes its own). */
  goTo?: (url: string) => void
}): ReactNode {
  const { booking, update } = useBooking()
  const heading = useFocusOnMount<HTMLHeadingElement>()
  const [keys, setKeys] = useState(newKeys)
  const [agreed, setAgreed] = useState(false)
  const [phase, setPhase] = useState<Phase>({ kind: 'review' })
  const [quote, setQuote] = useState<QuoteState>({ kind: 'loading' })
  const [quoteAttempt, setQuoteAttempt] = useState(0)
  // A second tap while the first is in flight sends nothing.
  const inFlight = useRef(false)

  const choice = tripChoice(booking)
  const choiceKey = choice ? JSON.stringify(choice) : null

  // The fare, fresh from the server every time the screen opens.
  useEffect(() => {
    if (!choiceKey) return
    const ctrl = new AbortController()
    fetchQuote(JSON.parse(choiceKey) as TripChoice, ctrl.signal).then(
      (q) => setQuote({ kind: 'ready', quote: q }),
      () => {
        if (!ctrl.signal.aborted) setQuote({ kind: 'failed' })
      },
    )
    return () => ctrl.abort()
  }, [choiceKey, quoteAttempt])

  // While the payer approves: ask every POLL_MS, until an answer or the
  // hold's end.
  const waitingRef = phase.kind === 'waiting' ? phase.reference : null
  const waitingUntil = phase.kind === 'waiting' ? phase.expiresAt : 0
  useEffect(() => {
    if (!waitingRef) return
    const ctrl = new AbortController()
    let timer: ReturnType<typeof setTimeout>
    const ask = () => {
      fetchCheckoutState(waitingRef, keys.manage, ctrl.signal).then(
        (s) => {
          if (ctrl.signal.aborted) return
          if (s.state === 'confirmed') {
            setPhase((p) => ({
              kind: 'confirmed',
              reference: waitingRef,
              // Taken when Pay was pressed: the booking is cleared now.
              carried: p.kind === 'waiting' ? p.carried : null,
            }))
            update({ ...EMPTY_BOOKING })
          } else if (s.state === 'waiting') {
            // Still waiting by the server's clock, which closes the hold
            // itself (T05): ask again, whatever this device's clock says.
            timer = setTimeout(ask, POLL_MS)
          } else if (s.state === 'closed') {
            setPhase({
              kind: 'refused',
              code: 'closed',
              message: 'This booking could not go ahead.',
              retry: null,
            })
          } else {
            setPhase({ kind: 'not_completed' })
          }
        },
        () => {
          // A blip while polling: ask again, until the hold has passed.
          if (ctrl.signal.aborted) return
          if (Date.now() < waitingUntil + GRACE_MS)
            timer = setTimeout(ask, POLL_MS)
          else setPhase({ kind: 'not_completed' })
        },
      )
    }
    timer = setTimeout(ask, POLL_MS)
    return () => {
      ctrl.abort()
      clearTimeout(timer)
    }
  }, [waitingRef, waitingUntil, keys.manage, update])

  const pay = (useKeys: Keys) => {
    const { trip, details, gatePassedAt } = booking
    if (inFlight.current || !choice || !trip || !details || !gatePassedAt)
      return
    inFlight.current = true
    setPhase({ kind: 'sending' })
    openCheckout(
      checkoutBody(
        gatePassedAt,
        booking,
        choice,
        trip,
        details,
        useKeys.manage,
      ),
      useKeys.idempotency,
    )
      .then(
        (opened) => {
          if (opened.payment_url) goTo(opened.payment_url)
          setPhase({
            kind: 'waiting',
            reference: opened.reference,
            expiresAt: Date.parse(opened.checkout_expires_at),
            carried: carriedFromBooking(booking),
          })
        },
        (err: unknown) => setPhase(refusalFor(err)),
      )
      .finally(() => {
        inFlight.current = false
      })
  }

  const tryAgain = (retry: 'same' | 'new') => {
    if (retry === 'same') {
      pay(keys)
      return
    }
    const fresh = newKeys()
    setKeys(fresh)
    pay(fresh)
  }

  if (phase.kind === 'confirmed') {
    // Screen 6 (M40-T09): the key this checkout was opened with is the
    // booking's private-link secret.
    const carriedTrip = phase.carried
    return (
      <ConfirmationScreen
        reference={phase.reference}
        manageKey={keys.manage}
        {...(carriedTrip && onBookAgain
          ? { onBookAgain: () => onBookAgain(carriedTrip) }
          : {})}
      />
    )
  }

  const { service, trip, details } = booking
  const area = options.areas.find((a) => a.id === trip?.pickupAreaId)
  const hospital = options.hospitals.find((h) => h.id === trip?.hospitalId)
  const pickup = trip ? new Date(trip.pickupAt) : null
  const total =
    quote.kind === 'ready' && quote.quote.bookable
      ? (quote.quote.total_pesewas ?? null)
      : null
  const busy = phase.kind === 'sending' || phase.kind === 'waiting'
  const canPay =
    agreed &&
    total !== null &&
    booking.gatePassedAt !== null &&
    !busy &&
    phase.kind !== 'not_completed'

  return (
    <PageShell measure={640} as="main">
      <TransportHeader onBack={onBack} />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          Review and pay
        </h1>
        <ProgressBar current={4} />

        <section aria-labelledby="rev-trip" className="flex flex-col gap-2">
          <h2 id="rev-trip" className={legend}>
            Your trip
          </h2>
          <Summary
            title="Service"
            onChange={() => onChange('service')}
            lines={[service ? SERVICE_NAME[service] : '']}
          />
          <Summary
            title="Passenger"
            onChange={() => onChange('details')}
            lines={passengerLines(booking)}
          />
          <Summary
            title="Pickup"
            onChange={() => onChange('trip')}
            lines={[
              area?.name ?? '',
              trip?.pickupAddress ?? '',
              pickup ? `${dayLabel(dayOf(pickup))}, ${timeOf(pickup)}` : '',
            ]}
          />
          <Summary
            title="Hospital"
            onChange={() => onChange('trip')}
            lines={[
              hospital?.name ?? '',
              trip?.appointmentAt
                ? `Appointment ${timeOf(new Date(trip.appointmentAt))}`
                : '',
            ]}
          />
          <Summary
            title={trip?.hireLength ? 'Hire' : 'Return'}
            onChange={() => onChange('trip')}
            lines={[returnLine(trip)]}
          />
        </section>

        <section aria-labelledby="rev-fare" className="flex flex-col gap-1">
          <h2 id="rev-fare" className={legend}>
            Fare
          </h2>
          <Fare
            quote={quote}
            onRetry={() => {
              setQuote({ kind: 'loading' })
              setQuoteAttempt((n) => n + 1)
            }}
          />
        </section>

        <div className="rounded-r3 border border-bed-warn bg-bed-warn-bg px-3 py-2.5 text-[12.5px] leading-relaxed text-ink-1">
          Free changes up to 24 hrs before pickup. Cancel more than 24 hrs
          ahead: refund minus admin fee. Within 24 hrs: 50% refund. If Medcor
          cannot provide your trip, you get a full refund automatically.
        </div>

        <p className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12.5px] leading-relaxed text-ink-1">
          This is a scheduled, non-emergency booking. In an emergency, call{' '}
          <a
            href={`tel:${EMERGENCY_NUMBER}`}
            onClick={onStop}
            onAuxClick={onStop}
            className="font-semibold text-sos underline underline-offset-2"
          >
            {EMERGENCY_NUMBER}
          </a>{' '}
          — do not wait for this vehicle.
        </p>

        <details className="rounded-r3 border border-line-1 px-3 py-2.5 text-[12.5px] text-ink-2">
          <summary className="cursor-pointer font-medium text-ink-1">
            Booking and cancellation terms
          </summary>
          <p className="mt-2 font-medium text-ink-1">
            Placeholder wording, to be approved by Medcor&apos;s lawyer before
            online booking opens.
          </p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            <li>
              Medcor Transport is for planned, non-emergency trips only. In an
              emergency, call {EMERGENCY_NUMBER}.
            </li>
            <li>Your booking is confirmed only once payment succeeds.</li>
            <li>Free changes up to 24 hours before pickup.</li>
            <li>
              Cancel more than 24 hours ahead: a refund minus an admin fee.
              Within 24 hours: a 50% refund.
            </li>
            <li>
              If Medcor cannot provide your trip, you get a full refund
              automatically.
            </li>
          </ul>
        </details>

        <label className="flex items-start gap-2.5 rounded-r3 border border-line-2 px-3 py-2.5 text-[13px] text-ink-1">
          <input
            type="checkbox"
            checked={agreed}
            disabled={busy}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          />
          <span className="font-medium">
            I agree to the booking and cancellation terms.
          </span>
        </label>

        <Button
          kind="primary"
          full
          disabled={!canPay}
          onClick={() => pay(keys)}
        >
          {total !== null ? `Pay ${formatPesewas(total)} & confirm` : 'Pay'}
        </Button>

        <Outcome
          phase={phase}
          onTryAgain={tryAgain}
          onChooseDay={() => onChange('trip')}
        />

        {details === null && (
          <p className="text-[12.5px] text-sos">
            Some answers are missing.{' '}
            <button
              type="button"
              className={linkButton}
              onClick={() => onChange('details')}
            >
              Go back to them
            </button>
          </p>
        )}
      </PageBand>
    </PageShell>
  )
}

/** What a refused Pay says, by the server's code (never "pending"). */
function refusalFor(err: unknown): Phase {
  if (!(err instanceof ApiError) || err.status === 0) {
    return {
      kind: 'refused',
      code: 'network_error',
      message: "We couldn't reach Medcor. Nothing was charged.",
      retry: 'same',
    }
  }
  switch (err.code) {
    case 'payment_not_connected':
      return {
        kind: 'refused',
        code: err.code,
        message: 'Online payment is not connected yet. Nothing was charged.',
        retry: null,
      }
    case 'day_full':
      return {
        kind: 'refused',
        code: err.code,
        message: 'That day has just filled up. Choose another day.',
        retry: null,
      }
    case 'checkout_closed':
      return {
        kind: 'refused',
        code: err.code,
        message: 'That payment was not completed. Nothing was charged.',
        retry: 'new',
      }
    case 'checkout_starting':
    case 'rate_limited':
    case 'payment_unavailable':
      return {
        kind: 'refused',
        code: err.code,
        message: err.message,
        retry: 'same',
      }
    default:
      return {
        kind: 'refused',
        code: err.code,
        message: err.message,
        retry: null,
      }
  }
}

function Outcome({
  phase,
  onTryAgain,
  onChooseDay,
}: {
  phase: Phase
  onTryAgain: (retry: 'same' | 'new') => void
  onChooseDay: () => void
}): ReactNode {
  let body: ReactNode = null
  if (phase.kind === 'sending') body = 'Opening your payment…'
  if (phase.kind === 'waiting') body = 'Waiting for your payment approval…'
  if (phase.kind === 'not_completed') {
    body = (
      <>
        Payment not completed. Nothing was charged.{' '}
        <button
          type="button"
          className={linkButton}
          onClick={() => onTryAgain('new')}
        >
          Try again
        </button>
      </>
    )
  }
  if (phase.kind === 'refused') {
    body = (
      <>
        {phase.message}
        {phase.code === 'payment_not_connected' && (
          <>
            {' '}
            Call the transport desk
            <Desk /> to book.
          </>
        )}
        {phase.code === 'day_full' && (
          <>
            {' '}
            <button type="button" className={linkButton} onClick={onChooseDay}>
              Choose another day
            </button>
          </>
        )}
        {phase.retry && (
          <>
            {' '}
            <button
              type="button"
              className={linkButton}
              onClick={() => onTryAgain(phase.retry ?? 'same')}
            >
              Try again
            </button>
          </>
        )}
      </>
    )
  }
  return (
    <p
      role="status"
      className="min-h-[1.25rem] text-[13px] leading-relaxed text-ink-1"
    >
      {body}
    </p>
  )
}

function Fare({
  quote,
  onRetry,
}: {
  quote: QuoteState
  onRetry: () => void
}): ReactNode {
  if (quote.kind === 'loading') {
    return <p className="text-[13px] text-ink-3">Checking the fare…</p>
  }
  if (quote.kind === 'failed') {
    return (
      <p className="text-[13px] text-ink-2">
        We couldn&apos;t check the fare.{' '}
        <button type="button" className={linkButton} onClick={onRetry}>
          Try again
        </button>
      </p>
    )
  }
  if (!quote.quote.bookable) {
    return (
      <p className="text-[13px] text-ink-2">
        This trip can&apos;t be booked online now. Call the transport desk
        <Desk />.
      </p>
    )
  }
  return (
    <dl className="flex flex-col gap-1 text-[13px]">
      {(quote.quote.lines ?? []).map((line, i) => (
        <div key={`${line.item}-${i}`} className="flex justify-between gap-3">
          <dt className="text-ink-2">
            {line.free ? `${line.label} — Free` : line.label}
          </dt>
          <dd className="tabular-nums text-ink-1">
            {line.free ? '' : formatPesewas(line.amount_pesewas)}
          </dd>
        </div>
      ))}
      <div className="mt-1 flex justify-between gap-3 border-t border-line-1 pt-1.5 font-semibold text-ink-1">
        <dt>Total</dt>
        <dd className="tabular-nums">
          {formatPesewas(quote.quote.total_pesewas ?? 0)}
        </dd>
      </div>
    </dl>
  )
}

function Summary({
  title,
  lines,
  onChange,
}: {
  title: string
  lines: string[]
  onChange: () => void
}): ReactNode {
  return (
    <div className="flex items-start justify-between gap-3 rounded-r3 border border-line-1 bg-bg-soft px-3 py-2.5">
      <div className="text-[13px] text-ink-1">
        <h3 className="text-[11.5px] font-normal text-ink-3">{title}</h3>
        {lines.filter(Boolean).map((l) => (
          <p key={l}>{l}</p>
        ))}
      </div>
      <button
        type="button"
        onClick={onChange}
        aria-label={`Change ${title.toLowerCase()}`}
        className={linkButton}
      >
        Change
      </button>
    </div>
  )
}

function passengerLines(booking: Booking): string[] {
  const d = booking.details
  const mobility =
    MOBILITY.find((m) => m.value === booking.mobility)?.label ?? ''
  if (!d) return [mobility]
  const who = d.passengerIsBooker
    ? `${d.bookerFirstName} ${d.bookerLastName}`
    : `${d.passengerName ?? ''}, ${d.passengerAge ?? ''}`
  return [who, mobility]
}

function returnLine(trip: Trip | null): string {
  if (!trip) return ''
  if (trip.hireLength)
    return trip.hireLength === 'full' ? 'Full day' : 'Half day'
  if (trip.journey !== 'return') return 'One way'
  if (trip.returnMode === 'call_to_collect')
    return 'Return: when discharged — call to collect'
  return trip.returnPickupAt
    ? `Return at ${timeOf(new Date(trip.returnPickupAt))}`
    : 'Return'
}
